import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import { ArrowLeft, Target, Video, ClipboardList, Calendar, Save, User, Check, Edit2, Camera, Trash2, Loader2, BarChart3, AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import PlayerDevelopmentProfile from '@/components/PlayerDevelopmentProfile';
import DevelopmentRadarChart from '@/components/DevelopmentRadarChart';
import AIEvaluationAssistant from '@/components/AIEvaluationAssistant';
import PlayerProfileView from '@/components/PlayerProfileView';
import SkillScoreStepper from '@/components/SkillScoreStepper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POSITIONS = [
  'Goalkeeper', 'Right Back', 'Left Back', 'Center Back', 
  'Defensive Midfielder', 'Central Midfielder', 'Attacking Midfielder',
  'Right Winger', 'Left Winger', 'Striker', 'Forward'
];

// Skills organized by category
const SKILL_CATEGORIES = [
  {
    category: 'TECHNICAL',
    color: 'text-emerald-400',
    skills: [
      { key: 'ball_control', label: 'Ball Control' },
      { key: 'passing', label: 'Passing' },
      { key: 'dribbling', label: 'Dribbling' },
      { key: 'first_touch', label: 'First Touch' },
      { key: 'finishing', label: 'Finishing' },
      { key: 'shooting', label: 'Shooting' },
    ]
  },
  {
    category: 'PHYSICAL',
    color: 'text-blue-400',
    skills: [
      { key: 'speed', label: 'Speed' },
      { key: 'acceleration', label: 'Acceleration' },
      { key: 'agility', label: 'Agility' },
      { key: 'coordination', label: 'Coordination' },
      { key: 'balance', label: 'Balance' },
      { key: 'explosiveness', label: 'Explosiveness' },
      { key: 'endurance', label: 'Endurance' },
    ]
  },
  {
    category: 'TACTICAL / IQ',
    color: 'text-yellow-400',
    skills: [
      { key: 'positioning', label: 'Positioning' },
      { key: 'decision_making', label: 'Decision Making' },
      { key: 'vision', label: 'Vision' },
      { key: 'game_awareness', label: 'Game Awareness' },
      { key: 'reaction_time', label: 'Reaction Time' },
    ]
  }
];

// Flatten skills for easy access
const ALL_SKILLS = SKILL_CATEGORIES.flatMap(cat => cat.skills);

// Related skills for consistency checking (same as Private Coach)
const RELATED_SKILLS = {
  ball_control: ['dribbling', 'first_touch'],
  dribbling: ['ball_control', 'agility', 'balance'],
  passing: ['vision', 'decision_making'],
  first_touch: ['ball_control'],
  finishing: ['shooting', 'decision_making'],
  shooting: ['finishing'],
  speed: ['acceleration', 'agility'],
  acceleration: ['speed', 'explosiveness'],
  agility: ['balance', 'coordination', 'speed'],
  coordination: ['agility', 'balance'],
  balance: ['coordination', 'agility'],
  explosiveness: ['acceleration', 'speed'],
  positioning: ['game_awareness', 'decision_making'],
  decision_making: ['vision', 'game_awareness', 'positioning'],
  vision: ['passing', 'decision_making', 'game_awareness'],
  game_awareness: ['positioning', 'vision', 'decision_making'],
  reaction_time: ['decision_making', 'game_awareness']
};

const TeamAthleteDetail = () => {
  const { teamId, athleteId } = useParams();
  const { currentTeam, isManager } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [athlete, setAthlete] = useState(null);
  const [techDev, setTechDev] = useState(null);
  const [trainingPlans, setTrainingPlans] = useState([]);
  const [videos, setVideos] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showConsistencyModal, setShowConsistencyModal] = useState(false);
  const [consistencyAlerts, setConsistencyAlerts] = useState([]);
  const photoInputRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('team_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  useEffect(() => {
    if (teamId && athleteId) {
      fetchAllData();
    }
  }, [teamId, athleteId]);

  const fetchAllData = async () => {
    try {
      const headers = getAuthHeaders();
      
      // Fetch athlete details
      const athleteRes = await fetch(
        `${API_URL}/api/clubs/team/${teamId}/athletes/${athleteId}`,
        { headers }
      );
      if (athleteRes.ok) {
        const athleteData = await athleteRes.json();
        setAthlete(athleteData);
      }

      // Fetch technical development
      const techRes = await fetch(
        `${API_URL}/api/clubs/team/${teamId}/athletes/${athleteId}/technical-development`,
        { headers }
      );
      if (techRes.ok) {
        const techData = await techRes.json();
        // Initialize with all skills, merging with existing data
        setTechDev(initializeTechDev(techData));
      } else {
        // Initialize empty tech dev
        setTechDev(initializeTechDev({}));
      }

      // Fetch training plans for this athlete
      const plansRes = await fetch(
        `${API_URL}/api/clubs/team/${teamId}/training-plans`,
        { headers }
      );
      if (plansRes.ok) {
        const allPlans = await plansRes.json();
        // Filter plans for this athlete or team-wide plans
        setTrainingPlans(allPlans.filter(p => !p.athlete_id || p.athlete_id === athleteId));
      }

      // Fetch videos for this athlete
      const videosRes = await fetch(
        `${API_URL}/api/clubs/team/${teamId}/videos`,
        { headers }
      );
      if (videosRes.ok) {
        const allVideos = await videosRes.json();
        setVideos(allVideos.filter(v => !v.athlete_id || v.athlete_id === athleteId));
      }

      // Fetch sessions for this athlete
      const sessionsRes = await fetch(
        `${API_URL}/api/clubs/team/${teamId}/athletes/${athleteId}/sessions`,
        { headers }
      );
      if (sessionsRes.ok) {
        const sessionsData = await sessionsRes.json();
        setSessions(sessionsData);
      }

    } catch (error) {
      console.error('Failed to load athlete data:', error);
      toast.error('Failed to load athlete data');
    }
    setLoading(false);
  };

  const initializeTechDev = (existingData = {}) => {
    const techDev = { ...existingData };
    ALL_SKILLS.forEach(skill => {
      if (!techDev[skill.key]) {
        techDev[skill.key] = { status: '', notes: '' };
      }
    });
    return techDev;
  };

  const saveAthleteInfo = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/clubs/team/${teamId}/athletes/${athleteId}`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            name: athlete.name,
            age: athlete.age,
            position: athlete.position
          })
        }
      );
      if (response.ok) {
        toast.success('Athlete updated');
        setEditMode(false);
      } else {
        toast.error('Failed to update athlete');
      }
    } catch (error) {
      toast.error('Failed to update athlete');
    }
    setSaving(false);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large (max 5MB)');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('team_token');
      const response = await fetch(
        `${API_URL}/api/clubs/team/${teamId}/athletes/${athleteId}/upload-photo`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAthlete(prev => ({ ...prev, photo_url: data.photo_url }));
        toast.success('Photo uploaded successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload photo');
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    }
    setUploadingPhoto(false);
    // Reset input
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  const handlePhotoDelete = async () => {
    if (!window.confirm('Remove athlete photo?')) return;

    setUploadingPhoto(true);
    try {
      const token = localStorage.getItem('team_token');
      const response = await fetch(
        `${API_URL}/api/clubs/team/${teamId}/athletes/${athleteId}/photo`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        setAthlete(prev => ({ ...prev, photo_url: '' }));
        toast.success('Photo removed');
      } else {
        toast.error('Failed to remove photo');
      }
    } catch (error) {
      toast.error('Failed to remove photo');
    }
    setUploadingPhoto(false);
  };

  // Check for inconsistencies in the evaluation (same as Private Coach)
  const checkConsistency = () => {
    if (!techDev) return [];
    
    const alerts = [];
    const STATUS_SCORE = { 'Focus': 1, 'Developing': 2, 'Proficient': 3 };
    
    // Check each skill against related skills
    Object.entries(RELATED_SKILLS).forEach(([skill, relatedSkills]) => {
      const skillData = techDev[skill];
      if (!skillData?.status) return;
      
      const skillScore = STATUS_SCORE[skillData.status];
      
      relatedSkills.forEach(relatedSkill => {
        const relatedData = techDev[relatedSkill];
        if (!relatedData?.status) return;
        
        const relatedScore = STATUS_SCORE[relatedData.status];
        const diff = Math.abs(skillScore - relatedScore);
        
        // Alert if there's a 2-level difference (e.g., Focus vs Proficient)
        if (diff >= 2) {
          const skillLabel = skill.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          const relatedLabel = relatedSkill.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          
          alerts.push({
            skill1: skillLabel,
            skill1Status: skillData.status,
            skill2: relatedLabel,
            skill2Status: relatedData.status,
            message: `${skillLabel} (${skillData.status}) and ${relatedLabel} (${relatedData.status}) are closely related skills with a significant gap.`
          });
        }
      });
    });
    
    // Remove duplicates (A-B and B-A)
    const seen = new Set();
    return alerts.filter(alert => {
      const key = [alert.skill1, alert.skill2].sort().join('-');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const saveTechDev = async (forceSave = false) => {
    // Check consistency before saving (skip for managers)
    if (!forceSave && !isManager) {
      const alerts = checkConsistency();
      if (alerts.length > 0) {
        setConsistencyAlerts(alerts);
        setShowConsistencyModal(true);
        return;
      }
    }
    
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/clubs/team/${teamId}/athletes/${athleteId}/technical-development`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(techDev)
        }
      );
      if (response.ok) {
        toast.success('Technical development updated');
        setShowConsistencyModal(false);
      } else {
        toast.error('Failed to update technical development');
      }
    } catch (error) {
      toast.error('Failed to update technical development');
    }
    setSaving(false);
  };

  // Handle AI suggestions application
  const handleApplyAISuggestions = (suggestions) => {
    setTechDev(prev => {
      const updated = { ...prev };
      
      // Apply technical suggestions
      Object.entries(suggestions.technical || {}).forEach(([key, value]) => {
        if (updated[key] !== undefined) {
          updated[key] = {
            ...updated[key],
            status: value.level,
            notes: value.note || updated[key]?.notes || ''
          };
        }
      });
      
      // Apply physical suggestions
      Object.entries(suggestions.physical || {}).forEach(([key, value]) => {
        if (updated[key] !== undefined) {
          updated[key] = {
            ...updated[key],
            status: value.level,
            notes: value.note || updated[key]?.notes || ''
          };
        }
      });
      
      // Apply tactical suggestions
      Object.entries(suggestions.tactical || {}).forEach(([key, value]) => {
        if (updated[key] !== undefined) {
          updated[key] = {
            ...updated[key],
            status: value.level,
            notes: value.note || updated[key]?.notes || ''
          };
        }
      });
      
      return updated;
    });
  };

  // Get auth headers for AI assistant (uses team token)
  const getAIAuthHeaders = () => {
    const token = localStorage.getItem('team_token');
    return () => ({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' });
  };

  // Score ranges by level (same as Private Coach)
  const SCORE_RANGES = {
    'Focus': { min: 4.0, max: 6.0, label: '4.0-6.0' },
    'Developing': { min: 6.1, max: 8.0, label: '6.1-8.0' },
    'Proficient': { min: 8.1, max: 10.0, label: '8.1-10.0' }
  };

  // Get default score for a level
  const getDefaultScoreForLevel = (level) => {
    const defaults = { 'Focus': 5.0, 'Developing': 7.0, 'Proficient': 8.3 };
    return defaults[level] || 7.0;
  };

  const updateSkillStatus = (skill, status) => {
    setTechDev(prev => ({
      ...prev,
      [skill]: { 
        ...prev[skill], 
        status,
        score: getDefaultScoreForLevel(status)
      }
    }));
  };

  const updateSkillScore = (skill, newScore) => {
    const currentStatus = techDev[skill]?.status;
    const range = SCORE_RANGES[currentStatus];
    if (!range) return;
    
    let score = parseFloat(newScore);
    if (isNaN(score)) return;
    
    // Clamp to valid range
    score = Math.max(range.min, Math.min(range.max, score));
    // Round to 1 decimal
    score = Math.round(score * 10) / 10;
    
    setTechDev(prev => ({
      ...prev,
      [skill]: { ...prev[skill], score }
    }));
  };

  const updateSkillNotes = (skill, notes) => {
    setTechDev(prev => ({
      ...prev,
      [skill]: { ...prev[skill], notes }
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Focus': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'Developing': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'Proficient': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'technical', label: 'Technical Dev', icon: Target },
    { id: 'profile', label: 'Player Profile', icon: BarChart3 },
    { id: 'training', label: 'Training Plans', icon: ClipboardList },
    { id: 'videos', label: 'Videos', icon: Video },
    { id: 'sessions', label: 'Sessions', icon: Calendar },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!athlete) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Athlete not found</p>
        <Link to={`/team/${teamId}/athletes`}>
          <Button className="mt-4">Back to Athletes</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-athlete-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to={`/team/${teamId}/athletes`} className="p-2 hover:bg-muted rounded-sm transition-colors">
          <ArrowLeft size={20} />
        </Link>
        
        {/* Athlete Photo */}
        <div className="relative group">
          {athlete.photo_url ? (
            <img 
              src={`${API_URL}${athlete.photo_url}`} 
              alt={athlete.name}
              className="w-16 h-16 rounded-full object-cover border-2 border-border"
            />
          ) : (
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center border-2 border-border">
              <span className="text-2xl font-bold text-primary">
                {athlete.name?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
          )}
          
          {/* Photo upload overlay - only for coaches */}
          {!isManager && (
            <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              {uploadingPhoto ? (
                <Loader2 size={20} className="text-white animate-spin" />
              ) : (
                <div className="flex gap-1">
                  <button 
                    onClick={() => photoInputRef.current?.click()}
                    className="p-1.5 bg-primary rounded-full text-black hover:bg-primary/80"
                    title="Upload photo"
                  >
                    <Camera size={14} />
                  </button>
                  {athlete.photo_url && (
                    <button 
                      onClick={handlePhotoDelete}
                      className="p-1.5 bg-red-500 rounded-full text-white hover:bg-red-600"
                      title="Remove photo"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoUpload}
            className="hidden"
          />
        </div>

        <div className="flex-1">
          <h1 className="font-heading text-3xl uppercase tracking-tight">{athlete.name}</h1>
          <p className="text-muted-foreground">
            {athlete.position || 'No position'} • {athlete.age ? `${athlete.age} years` : 'Age not set'} • Soccer
          </p>
        </div>
      </div>

      {/* Stats Summary Card */}
      <div className="bg-card border border-border/40 p-5 rounded-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-muted-foreground uppercase">Team</p>
            <p className="font-semibold">{currentTeam?.team_name}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase">Training Plans</p>
            <p className="font-semibold">{trainingPlans.length}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase">Videos</p>
            <p className="font-semibold">{videos.length}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase">Sessions</p>
            <p className="font-semibold">{sessions.length}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm uppercase tracking-wider whitespace-nowrap transition-colors ${
              activeTab === tab.id ? 'bg-primary text-black' : 'bg-card border border-border hover:bg-muted'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-card border border-border/40 rounded-sm p-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl uppercase">Athlete Information</h2>
              {!isManager && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setEditMode(!editMode)}
                >
                  <Edit2 size={14} className="mr-1" />
                  {editMode ? 'Cancel' : 'Edit'}
                </Button>
              )}
            </div>
            
            {editMode ? (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">Name</label>
                  <Input 
                    value={athlete.name || ''} 
                    onChange={(e) => setAthlete({...athlete, name: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Age</label>
                  <Input 
                    type="number" 
                    value={athlete.age || ''} 
                    onChange={(e) => setAthlete({...athlete, age: parseInt(e.target.value) || 0})} 
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Position</label>
                  <select 
                    value={athlete.position || ''} 
                    onChange={(e) => setAthlete({...athlete, position: e.target.value})} 
                    className="w-full p-2 bg-transparent border border-border rounded-sm"
                  >
                    <option value="" className="bg-card">Select Position</option>
                    {POSITIONS.map(pos => (
                      <option key={pos} value={pos} className="bg-card">{pos}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Button onClick={saveAthleteInfo} disabled={saving} className="bg-primary text-black">
                    <Save className="mr-2 h-4 w-4" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Full Name</p>
                  <p className="font-medium">{athlete.name}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Age</p>
                  <p className="font-medium">{athlete.age ? `${athlete.age} years` : 'Not set'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Position</p>
                  <p className="font-medium">{athlete.position || 'Not set'}</p>
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="mt-8">
              <h3 className="font-heading text-lg uppercase mb-4">Development Summary</h3>
              <div className="grid grid-cols-3 gap-4">
                {['Focus', 'Developing', 'Proficient'].map(status => {
                  const count = techDev ? Object.values(techDev).filter(s => s.status === status).length : 0;
                  return (
                    <div key={status} className={`p-4 rounded-sm text-center ${getStatusColor(status)}`}>
                      <p className="text-2xl font-bold">{count}</p>
                      <p className="text-sm">{status}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Technical Development Tab */}
        {activeTab === 'technical' && (
          <div className="space-y-6">
            <h2 className="font-heading text-xl uppercase">Technical Development</h2>
            
            {isManager ? (
              <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded-sm">
                <p className="text-sm text-blue-400">
                  As a Team Manager, you can view but not edit technical development data.
                </p>
              </div>
            ) : (
              /* AI Evaluation Assistant - Only for Coaches */
              <AIEvaluationAssistant
                athleteName={athlete?.name}
                athletePosition={athlete?.position}
                athleteAge={athlete?.age}
                onApplySuggestions={handleApplyAISuggestions}
                getAuthHeaders={getAIAuthHeaders()}
                coachType="team"
              />
            )}

            {/* Radar Chart - Development Visualization */}
            {techDev && <DevelopmentRadarChart techDev={techDev} />}

            {/* Player Development Profile Summary */}
            {techDev && <PlayerDevelopmentProfile techDev={techDev} />}

            {/* Status Legend with Score Ranges */}
            <div className="bg-muted/30 p-3 rounded-sm">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="text-muted-foreground">Status (Score Range):</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"></span> Focus (4.0-6.0)</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400"></span> Developing (6.1-8.0)</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400"></span> Proficient (8.1-10.0)</span>
              </div>
            </div>

            {techDev && SKILL_CATEGORIES.map((category) => (
              <div key={category.category}>
                <div className="flex items-center gap-3 mb-4">
                  <h3 className={`font-heading text-lg uppercase tracking-wider ${category.color}`}>
                    {category.category}
                  </h3>
                  <div className="flex-1 h-px bg-border"></div>
                </div>
                <div className="space-y-4 mb-6">
                  {category.skills.map((skill) => {
                    const currentStatus = techDev[skill.key]?.status;
                    const range = SCORE_RANGES[currentStatus];
                    return (
                      <div key={skill.key} className="border border-border/40 p-4 rounded-sm">
                        <div className="flex items-center justify-between mb-3">
                          <p className="font-medium">{skill.label}</p>
                          <div className="flex items-center gap-3">
                            {/* Status buttons */}
                            <div className="flex gap-2">
                              {['Focus', 'Developing', 'Proficient'].map((status) => (
                                <button
                                  key={status}
                                  onClick={() => !isManager && updateSkillStatus(skill.key, status)}
                                  disabled={isManager}
                                  className={`px-3 py-1 text-xs rounded-sm uppercase transition-colors ${
                                    currentStatus === status
                                      ? getStatusColor(status)
                                      : 'bg-muted hover:bg-muted/80'
                                  } ${isManager ? 'cursor-not-allowed opacity-60' : ''}`}
                                >
                                  {status}
                                </button>
                              ))}
                            </div>
                            {/* Score input */}
                            {currentStatus && range && !isManager && (
                              <SkillScoreStepper
                                skillKey={skill.key}
                                value={techDev[skill.key]?.score || getDefaultScoreForLevel(currentStatus)}
                                min={range.min}
                                max={range.max}
                                rangeLabel={range.label}
                                onCommit={(k, v) => updateSkillScore(k, v)}
                                dataTestId={`score-input-${skill.key}`}
                              />
                            )}
                            {currentStatus && range && isManager && (
                              <span className="text-sm text-muted-foreground">
                                {techDev[skill.key]?.score?.toFixed(1) || getDefaultScoreForLevel(currentStatus)} ({range.label})
                              </span>
                            )}
                          </div>
                        </div>
                        <Textarea
                          placeholder="Coach notes for this skill..."
                          value={techDev[skill.key]?.notes || ''}
                          onChange={(e) => !isManager && updateSkillNotes(skill.key, e.target.value)}
                          disabled={isManager}
                          className="bg-transparent"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {!isManager && (
              <Button onClick={() => saveTechDev(false)} disabled={saving} className="bg-primary text-black">
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Saving...' : 'Save Development'}
              </Button>
            )}
          </div>
        )}

        {/* Player Profile Tab - Compiled profile view */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <h2 className="font-heading text-xl uppercase">Player Development Profile</h2>
            <p className="text-muted-foreground text-sm">
              Compiled profile based on evaluations from Private Coach and Team Coach with weighted metrics.
            </p>
            <PlayerProfileView 
              athleteId={athleteId}
              athleteName={athlete?.name}
              showRadar={true}
            />
          </div>
        )}

        {/* Training Plans Tab */}
        {activeTab === 'training' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl uppercase">Training Plans</h2>
              {!isManager && (
                <Link to={`/team/${teamId}/training-plans`}>
                  <Button variant="outline" size="sm">Manage Plans</Button>
                </Link>
              )}
            </div>
            
            {trainingPlans.length === 0 ? (
              <div className="text-center py-8">
                <ClipboardList className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No training plans assigned</p>
                {!isManager && (
                  <Link to={`/team/${teamId}/training-plans`}>
                    <Button>Create Training Plan</Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {trainingPlans.map((plan) => (
                  <div key={plan.id} className="border border-border/40 p-4 rounded-sm">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold">{plan.name}</p>
                        <p className="text-sm text-muted-foreground capitalize">{plan.plan_type} Plan</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        plan.athlete_id ? 'bg-primary/20 text-primary' : 'bg-muted'
                      }`}>
                        {plan.athlete_id ? 'Individual' : 'Team-wide'}
                      </span>
                    </div>
                    {plan.objectives?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-muted-foreground uppercase mb-2">Objectives:</p>
                        <ul className="space-y-1">
                          {plan.objectives.map((obj, idx) => (
                            <li key={idx} className="text-sm flex items-center gap-2">
                              <Target size={12} className="text-primary" />
                              {obj}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {plan.drill_ids?.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        {plan.drill_ids.length} drill(s) assigned
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl uppercase">Video Analysis</h2>
              {!isManager && (
                <Link to={`/team/${teamId}/videos`}>
                  <Button variant="outline" size="sm">Manage Videos</Button>
                </Link>
              )}
            </div>
            
            {videos.length === 0 ? (
              <div className="text-center py-8">
                <Video className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No videos added</p>
                {!isManager && (
                  <Link to={`/team/${teamId}/videos`}>
                    <Button>Add Video</Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {videos.map((video) => (
                  <div key={video.id} className="border border-border/40 p-4 rounded-sm">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium">{video.title}</p>
                        <p className="text-sm text-muted-foreground capitalize">
                          {video.video_type?.replace('_', ' ')}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        video.athlete_id ? 'bg-primary/20 text-primary' : 'bg-muted'
                      }`}>
                        {video.athlete_id ? 'Individual' : 'Team'}
                      </span>
                    </div>
                    {video.description && (
                      <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed line-clamp-6 mb-2">{video.description}</p>
                    )}
                    <a 
                      href={video.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-primary text-sm hover:underline"
                    >
                      Watch Video →
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl uppercase">Session History</h2>
              {!isManager && (
                <Link to={`/team/${teamId}/sessions`}>
                  <Button variant="outline" size="sm">Manage Sessions</Button>
                </Link>
              )}
            </div>
            
            {sessions.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No sessions recorded</p>
                {!isManager && (
                  <Link to={`/team/${teamId}/sessions`}>
                    <Button>Schedule Session</Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {sessions.slice(0, 10).map((session) => (
                  <div key={session.id} className="border border-border/40 p-4 rounded-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium capitalize">{session.session_type || 'Training'} Session</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(session.date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                        session.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        session.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {session.status || 'scheduled'}
                      </span>
                    </div>
                    {session.notes && (
                      <p className="text-sm text-muted-foreground mt-2">{session.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Consistency Check Modal */}
      {showConsistencyModal && consistencyAlerts.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-amber-400" size={24} />
                <h3 className="font-heading text-lg">Consistency Check</h3>
              </div>
              <button onClick={() => setShowConsistencyModal(false)} className="text-muted-foreground hover:text-foreground">
                <X size={20} />
              </button>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              We detected {consistencyAlerts.length} potential inconsistencies in your evaluation. 
              Please review before saving.
            </p>
            
            <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
              {consistencyAlerts.map((alert, idx) => (
                <div key={idx} className="bg-amber-500/10 border border-amber-500/20 rounded p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      alert.skill1Status === 'Focus' ? 'bg-red-500/20 text-red-400' :
                      alert.skill1Status === 'Developing' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-emerald-500/20 text-emerald-400'
                    }`}>
                      {alert.skill1}: {alert.skill1Status}
                    </span>
                    <span className="text-muted-foreground">vs</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      alert.skill2Status === 'Focus' ? 'bg-red-500/20 text-red-400' :
                      alert.skill2Status === 'Developing' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-emerald-500/20 text-emerald-400'
                    }`}>
                      {alert.skill2}: {alert.skill2Status}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{alert.message}</p>
                </div>
              ))}
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowConsistencyModal(false)}>
                Review Evaluation
              </Button>
              <Button 
                onClick={() => saveTechDev(true)} 
                disabled={saving}
                className="bg-primary text-black"
              >
                {saving ? 'Saving...' : 'Save Anyway'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamAthleteDetail;
